// cache.js

const DEFAULT_TTL = 5 * 60 * 1000;

function getDefaultStorage() {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {() => Promise<any>} fetcher
 * @param {{
 *   ttl?: number,
 *   refreshOnGet?: boolean,
 *   storage?: Storage,
 *   keyPrefix?: string
 * }} [options]
 */
export function useCache(key, fetcher, options = {}) {
  const { ttl = DEFAULT_TTL, refreshOnGet = true, storage = getDefaultStorage(), keyPrefix = 'cache:' } = options;

  const storageKey = `${keyPrefix}${key}`;

  const state = {
    data: null,
    isLoading: false,
    error: null,
    savedAt: null
  };

  let lastSavedAt = null;

  const listeners = new Set();

  const emit = () => {
    const snapshot = { ...state };
    listeners.forEach((fn) => fn(snapshot));
  };

  const isExpired = (savedAt) => ttl > 0 && Date.now() - savedAt > ttl;

  const read = () => {
    if (!storage) return null;

    const raw = storage.getItem(storageKey);
    if (!raw) return null;

    const stored = safeParse(raw);
    if (!stored || typeof stored !== 'object') {
      try {
        storage.removeItem(storageKey);
      } catch {}
      return null;
    }

    const savedAt = stored.savedAt;
    if (typeof savedAt !== 'number' || !('value' in stored)) {
      try {
        storage.removeItem(storageKey);
      } catch {}
      return null;
    }

    if (isExpired(savedAt)) {
      try {
        storage.removeItem(storageKey);
      } catch {}
      return null;
    }

    lastSavedAt = savedAt;
    return stored.value;
  };

  const write = (value) => {
    if (!storage) return;
    try {
      const savedAt = lastSavedAt ?? Date.now();
      storage.setItem(storageKey, JSON.stringify({ value, savedAt }));
    } catch {
      try {
        storage.removeItem(storageKey);
      } catch {}
    }
  };

  const setData = (value) => {
    state.data = value;
    lastSavedAt = Date.now();
    state.savedAt = lastSavedAt;
    write(value);
    emit();
  };

  // init from storage
  state.data = read();
  state.savedAt = lastSavedAt;

  const refreshData = async (silent = false) => {
    if (!silent) {
      state.isLoading = true;
      emit();
    }
    state.error = null;
    emit();

    try {
      const fresh = await fetcher();
      setData(fresh);
      return fresh;
    } catch (err) {
      state.error = err;
      emit();
      throw err;
    } finally {
      if (!silent) {
        state.isLoading = false;
        emit();
      }
    }
  };

  const get = async (opts) => {
    const shouldRevalidate = opts && typeof opts.revalidate === 'boolean' ? opts.revalidate : refreshOnGet;

    const hasExpired = lastSavedAt !== null && isExpired(lastSavedAt);

    if (state.data !== null && !hasExpired) {
      if (shouldRevalidate) refreshData(true).catch(() => undefined);
      return state.data;
    }

    if (hasExpired) {
      state.data = null;
      state.savedAt = null;
      lastSavedAt = null;
      try {
        storage && storage.removeItem(storageKey);
      } catch {}
    }

    return refreshData(false);
  };

  const clear = () => {
    state.data = null;
    state.isLoading = false;
    state.error = null;
    state.savedAt = null;
    lastSavedAt = null;
    emit();
    try {
      storage && storage.removeItem(storageKey);
    } catch {}
  };

  // cross-tab sync (browser only)
  let stopStorageListener = () => {};
  if (typeof window !== 'undefined' && storage) {
    const handler = (event) => {
      if (event.storageArea !== storage || event.key !== storageKey) return;

      if (!event.newValue) {
        state.data = null;
        state.savedAt = null;
        lastSavedAt = null;
        emit();
        return;
      }

      const stored = safeParse(event.newValue);
      if (!stored || typeof stored.savedAt !== 'number') {
        try {
          storage.removeItem(storageKey);
        } catch {}
        state.data = null;
        state.savedAt = null;
        lastSavedAt = null;
        emit();
        return;
      }

      if (isExpired(stored.savedAt)) {
        try {
          storage.removeItem(storageKey);
        } catch {}
        state.data = null;
        state.savedAt = null;
        lastSavedAt = null;
        emit();
        return;
      }

      state.data = stored.value;
      state.savedAt = stored.savedAt;
      lastSavedAt = stored.savedAt;
      emit();
    };

    window.addEventListener('storage', handler);
    stopStorageListener = () => window.removeEventListener('storage', handler);
  }

  return {
    // state access
    getState: () => ({ ...state }),

    // subscribe for UI frameworks
    subscribe: (listener) => {
      listeners.add(listener);
      listener({ ...state });
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stopStorageListener();
      };
    },

    // actions
    get,
    refresh: () => refreshData(false),
    clear,
    set: (value) => setData(value)
  };
}
